import { forwardRef, useImperativeHandle, useState } from 'react';
import { useIntl } from 'react-intl';

import { CalendarIcon } from '@dynatrace/strato-icons';

import type { DateSelectorProps, DateSelectorRef } from './types.js';
import { Button } from '../../buttons/button/Button.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isVisualRefreshEnabled } from '../../core/utils/is-visual-refresh-enabled.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';
import { CalendarRoot } from '../calendar/CalendarRoot.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';

/**
 * Calendar widget trigger and overlay component.
 */
export const DateSelector = forwardRef<DateSelectorRef, DateSelectorProps>(
  (props, forwardedRef) => {
    const {
      disabled,
      error,
      readOnly,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': ariaDisabledProp,
      ...remainingProps
    } = props;
    const [open, setOpen] = useState(false);

    const {
      targetRef,
      triggerRef,
      overlayRef,
      overlayState,
      overlayProps,
      overlayTriggerProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-end',
      offset: 8,
      trapFocus: false,
      shouldCloseOnBlur: false,
      open,
      onOpenChange: setOpen,
      // the overlay should not open when disabled or readOnly
      disabled: disabled || readOnly,
    });

    const rootRef = useMergeRefs<HTMLButtonElement>([triggerRef, targetRef]);
    const intl = useIntl();
    const calendarInputLabel = intl.formatMessage({
      defaultMessage: 'Calendar for picking a date',
      id: 'qpf+aqg3hXoviypu',
      description:
        'Aria label for the DateTimePicker calendar input used for form validation.',
    });

    useImperativeHandle(
      forwardedRef,
      (): DateSelectorRef => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
      }),
      [],
    );

    const { state: formFieldState } = useFormFieldMessagesContext();

    return (
      <>
        <Button
          ref={rootRef}
          data-testid={dataTestId}
          className={consumerClassName}
          aria-disabled={ariaDisabledProp}
          style={consumerStyle}
          {...remainingProps}
          {...overlayTriggerProps}
          size="condensed"
          color={error ? 'critical' : 'neutral'}
          variant={isVisualRefreshEnabled() ? 'emphasized' : 'default'}
          data-dt-name="ShowCalendar"
        >
          <Button.Prefix>
            <CalendarIcon />
          </Button.Prefix>
        </Button>
        <Overlay
          ref={overlayRef}
          isOpen={overlayState.isOpen}
          className={globalSprinkles({
            paddingLeft: 16,
            paddingTop: 16,
            paddingRight: 16,
            paddingBottom: 16,
          })}
          overlayProps={overlayProps}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          aria-label={intl.formatMessage({
            defaultMessage: 'Calendar overlay for picking a date',
            id: 'A9Pdb5T48IHfEauq',
            description: 'Aria label for the date time picker overlay.',
          })}
        >
          {/* The Calendar should not consume any validation context to not dispatch any errors. We still want the errorstate to adjust the day highlighting. */}
          <FormFieldMessageContext.Provider
            value={{
              state: {
                customMessages: [],
                formControlMessages: [],
                showError: formFieldState.showError,
              },
            }}
          >
            <CalendarRoot
              aria-label={calendarInputLabel}
              aria-disabled={ariaDisabledProp}
            />
          </FormFieldMessageContext.Provider>
        </Overlay>
      </>
    );
  },
);
